'use client';

import React, { useEffect } from 'react';

import { Button } from '@/core/presentation/components/ui/Button';
import {
  LEGAL_DOCUMENT_VIEWER_CLOSE_LABEL,
  LEGAL_DOCUMENT_VIEWER_OVERLAY_CLOSE_ARIA,
} from '@/core/presentation/constants/legal-document-viewer-copy';

export interface LegalDocumentViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  /** Texto exibido (ex.: conteúdo atual vindo do `AppContent` via contexto no pai). */
  body: string;
  /** Sufixo estável para ids ARIA (ex.: `terms`, `privacy`). */
  contentInstanceId: string;
}

export const LegalDocumentViewerModal: React.FC<LegalDocumentViewerModalProps> = ({
  isOpen,
  onClose,
  title,
  body,
  contentInstanceId,
}) => {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const titleId = `legal-document-viewer-title-${contentInstanceId}`;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <button
        type="button"
        aria-label={LEGAL_DOCUMENT_VIEWER_OVERLAY_CLOSE_ARIA}
        onClick={onClose}
        className="absolute inset-0 bg-black/55 transition-opacity"
      />
      <div
        className="relative flex max-h-[min(92dvh,720px)] w-full flex-col overflow-hidden rounded-t-2xl border border-border-light bg-white shadow-xl sm:mt-0 sm:max-w-2xl sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-border-light px-4 py-4 sm:px-6">
          <h2 id={titleId} className="text-lg font-semibold leading-snug text-navy-dark pr-2">
            {title}
          </h2>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={onClose}
          >
            {LEGAL_DOCUMENT_VIEWER_CLOSE_LABEL}
          </Button>
        </div>
        <div
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6 sm:py-5 pb-[calc(1.25rem+env(safe-area-inset-bottom,0px))]"
          tabIndex={0}
        >
          <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-relaxed text-text-body">
            {body}
          </pre>
        </div>
      </div>
    </div>
  );
};
